import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { discard, pending, pendingMedia, rejected, rejectedMedia, retryRejected, subscribe } from '../lib/outbox.js';
import { sync } from '../lib/sync.js';
import type { OutboxEntry, PendingMedia } from '../lib/localdb.js';
import { shortTime } from '../lib/format.js';
import { BigButton } from '../components/Controls.js';

/**
 * Work that has not reached the office.
 *
 * A count you cannot inspect is just anxiety, so this screen exists the moment anything is
 * queued. Two lists, and the difference between them is the whole point:
 *
 *   - **En attente** — saved on this phone, waiting for signal. Nothing is wrong.
 *   - **À régler** — the server refused it for a reason retrying cannot fix, and a person has
 *     to look. Usually the office moved the stop while the phone was underground.
 *
 * Nothing is ever silently dropped. Discarding is possible and is a deliberate act with the
 * reason in front of you.
 */
export function PendingScreen() {
  const [queue, setQueue] = useState<OutboxEntry[]>([]);
  const [failed, setFailed] = useState<OutboxEntry[]>([]);
  const [media, setMedia] = useState<PendingMedia[]>([]);
  const [failedMedia, setFailedMedia] = useState<PendingMedia[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      setQueue(await pending());
      setFailed(await rejected());
      setMedia(await pendingMedia());
      setFailedMedia(await rejectedMedia());
    };
    void load();
    return subscribe(() => void load());
  }, []);

  const total = queue.length + media.length;

  return (
    <div className="pendingscreen">
      <header className="stopscreen__head">
        <Link to="/" className="back" aria-label="Retour">
          ‹
        </Link>
        <div className="stopscreen__title">
          <h1>Envois</h1>
          <p>
            {total} en attente{failed.length + failedMedia.length > 0 ? ` · ${failed.length + failedMedia.length} à régler` : ''}
          </p>
        </div>
      </header>

      <BigButton
        onClick={async () => {
          setBusy(true);
          try {
            await sync();
          } finally {
            setBusy(false);
          }
        }}
        busy={busy}
        disabled={!navigator.onLine}
        hint={navigator.onLine ? undefined : 'Aucun signal'}
      >
        Envoyer maintenant
      </BigButton>

      {(failed.length > 0 || failedMedia.length > 0) && (
        <section className="pendingscreen__block pendingscreen__block--failed">
          <h2>À régler</h2>
          <ul>
            {failed.map((entry) => (
              <li key={entry.id}>
                <p className="pending__label">{entry.label}</p>
                <p className="warn">{entry.rejectedReason}</p>
                <div className="pending__actions">
                  <button type="button" className="ghost" onClick={() => void retryRejected(entry.id)}>
                    Réessayer
                  </button>
                  <button type="button" className="ghost ghost--danger" onClick={() => void discard(entry.id)}>
                    Abandonner
                  </button>
                </div>
              </li>
            ))}
            {failedMedia.map((item) => (
              <li key={item.id}>
                <p className="pending__label">Photo · {item.kind}</p>
                <p className="warn">{item.rejectedReason}</p>
                <div className="pending__actions">
                  <button type="button" className="ghost" onClick={() => void retryRejected(item.id)}>
                    Réessayer
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="pendingscreen__block">
        <h2>En attente</h2>
        {total === 0 && <p className="quiet">Tout est à l’office.</p>}
        <ul>
          {queue.map((entry) => (
            <li key={entry.id}>
              <p className="pending__label">{entry.label}</p>
              <p className="meta">
                {shortTime(entry.createdAt)}
                {entry.attempts > 0 ? ` · ${entry.attempts} tentative${entry.attempts > 1 ? 's' : ''}` : ''}
                {entry.awaitsMedia ? ' · attend une photo' : ''}
              </p>
            </li>
          ))}
          {media.map((item) => (
            <li key={item.id}>
              <p className="pending__label">Photo · {item.kind}</p>
              <p className="meta">
                {shortTime(item.createdAt)} ·{' '}
                {item.blob.size > 0 ? `${Math.round((item.offset / item.blob.size) * 100)} %` : '0 %'}
              </p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
